import { useEffect, useState, useCallback, useRef } from 'react';
import './styles.scss';
import { useSelector, useDispatch } from 'react-redux';
import { PlusIcon, FadersHorizontalIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { updateHeader } from '../../slices/header-slice';
import { updateSlabsData } from '../../slices/app-slice';
import { getSlabs } from '../../api';
import { useDataLoading } from '../../hooks/useDataLoading';

import { LazyLoadedGroup } from '../../components/lazy-loaded-group';
import { groupArrayInChunks, createSlug } from '../../utility/utils';
import FilterOverlay from '../../components/filter-overlay';


const Slabs = ({ type }) => {
    const navigate = useNavigate();
    const slabs = useSelector((state) => state.app.slabs);
    const announcement = useSelector((state) => state.app.homepage?.announcement);
    const dispatch = useDispatch();
    const [filters, setFilters] = useState([]);
    const [species, setSpecies] = useState([]);
    const [tones, setTones] = useState([]);
    const [grains, setGrains] = useState([]);
    const [activeFilter, setActiveFilter] = useState(null);
    const [activeSpecies, setActiveSpecies] = useState("");
    const [activeTone, setActiveTone] = useState("");
    const [activeGrain, setActiveGrain] = useState("");
    const [activeLength, setActiveLength] = useState("");
    const [filterResults, setFilterResults] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);

    // Loading state for slabs data
    const [isSlabsLoading, setIsSlabsLoading] = useState(false);
    const hasFetched = useRef(false);

    // Register loading state with global spinner
    useDataLoading('slabs', isSlabsLoading, []);

    // Fetch slabs data if not already populated
    useEffect(() => {
        const fetchSlabsData = async () => {
            // Check if slabs have actual data (not just empty array)
            const hasData = slabs && Array.isArray(slabs) && slabs.length > 0;

            if (!hasData && !hasFetched.current) {
                hasFetched.current = true;
                setIsSlabsLoading(true);
                try {
                    const response = await getSlabs();
                    dispatch(updateSlabsData(response.data));
                } catch (error) {
                    console.error('Error fetching slabs data:', error);
                } finally {
                    setIsSlabsLoading(false);
                }
            }
        };

        fetchSlabsData();
    }, [slabs, dispatch]);

    // Build filter configuration for FilterOverlay
    const filterConfig = {
        category: {
            label: 'Category',
            type: 'radio',
            options: filters.map(filter => ({
                value: filter,
                label: filter
            }))
        },
        species: {
            label: 'Species',
            type: 'select',
            options: species.map(s => ({
                value: s,
                label: s
            }))
        },
        tone: {
            label: 'Tone',
            type: 'select',
            options: tones.map(t => ({
                value: t,
                label: t
            }))
        },
        grain: {
            label: 'Grain',
            type: 'select',
            options: grains.map(g => ({
                value: g,
                label: g
            }))
        },
        length: {
            label: 'Length',
            type: 'select',
            options: [
                { value: 'wall-art', label: 'Wall Art (3\' or less)' },
                { value: 'coffee-tables', label: 'Coffee Tables (3\'-6\')' },
                { value: 'dining-tables', label: 'Dining Tables (6\'+)' }
            ]
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        dispatch(updateHeader('light'));

        if (slabs && Array.isArray(slabs) && slabs.length > 0) {
            const categories = slabs.map(x => x?.filter).filter(Boolean);
            const unique = categories.filter((c, index) => categories.indexOf(c) === index);

            const speciesOptions = slabs.map(x => x?.species).filter(Boolean);
            const uniqueSpecies = speciesOptions.filter((s, index) => speciesOptions.indexOf(s) === index)
                .filter(species => species && species.trim() !== '')  // Filter out empty values
                .sort((a, b) => a.localeCompare(b));  // Sort alphabetically

            const toneOptions = slabs.map(x => x?.tone).filter(Boolean);
            const uniqueTones = toneOptions.filter((t, index) => toneOptions.indexOf(t) === index)
                .filter(tone => tone && tone.trim() !== '')  // Filter out empty values
                .sort((a, b) => a.localeCompare(b));  // Sort alphabetically

            const grainOptions = slabs.map(x => x?.grain).filter(Boolean);
            const uniqueGrains = grainOptions.filter((g, index) => grainOptions.indexOf(g) === index)
                .filter(grain => grain && grain.trim() !== '')  // Filter out empty values
                .sort((a, b) => a.localeCompare(b));  // Sort alphabetically

            setFilters(unique);
            setSpecies(uniqueSpecies);
            setTones(uniqueTones);
            setGrains(uniqueGrains);
            setFilterResults(groupArrayInChunks(slabs, 6));
        }
    }, [dispatch, slabs, type]);

    useEffect(() => {
        if (!slabs || !Array.isArray(slabs) || slabs.length === 0) {
            setFilterResults([]);
            return;
        }

        let result = [...slabs]; // Create a copy to avoid mutating original array

        if (activeFilter) {
            result = result.filter(x => x?.filter === activeFilter);
        }

        if (activeSpecies) {
            result = result.filter(x => x?.species === activeSpecies);
        }

        if (activeTone) {
            result = result.filter(x => x?.tone === activeTone);
        }

        if (activeGrain) {
            result = result.filter(x => x?.grain === activeGrain);
        }

        if (activeLength) {
            if (activeLength === "wall-art") {
                result = result.filter(x => x?.length && x.length <= 36);
            } else if (activeLength === "coffee-tables") {
                result = result.filter(x => x?.length && x.length > 36 && x.length <= 72);
            } else if (activeLength === "dining-tables") {
                result = result.filter(x => x?.length && x.length > 72);
            }
        }

        setFilterResults(groupArrayInChunks(result, 6));
    }, [activeFilter, activeSpecies, activeTone, activeGrain, activeLength, slabs]);


    const handleFilterApply = useCallback((appliedFilters) => {
        setActiveFilter(appliedFilters.category || null);
        setActiveSpecies(appliedFilters.species || "");
        setActiveTone(appliedFilters.tone || "");
        setActiveGrain(appliedFilters.grain || "");
        setActiveLength(appliedFilters.length || "");
    }, []);

    const onProductClick = (item) => {
        if (!item?.id || !item?.title) return; // Safety check

        const titleSlug = createSlug(item.title);
        navigate({
            pathname: `/slabs/${item.id}`,
            search: `?title=${titleSlug}`,
        });
    };

    return (
        <div className={`slabs-page ${announcement ? 'has-announcement' : ''}`}>
            <div className="store-intro">
                <h1>Live Edge Slabs.</h1>
                <p>We offer a range of Live Edge Slabs listed below that can be purchased separately for your project needs...</p>
            </div>

            <div className="tools-wrap">
                <div className="tools">
                    <div className="filter" onClick={() => setFilterOpen(!filterOpen)}>
                        <span><span> Filters <PlusIcon size={16} /> </span><span>Sort <FadersHorizontalIcon size={24} /></span></span>
                    </div>
                    <div className="sort"></div>
                </div>
            </div>

            <FilterOverlay
                isOpen={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                filters={filterConfig}
                activeFilters={{
                    category: activeFilter,
                    species: activeSpecies,
                    tone: activeTone,
                    grain: activeGrain,
                    length: activeLength
                }}
                title="SLABS"
            />

            <div className='lazy-load'>
                {filterResults &&
                    filterResults.map((group, i) => (
                        <LazyLoadedGroup
                            key={i}
                            group={group}
                            index={i}
                            type={type}
                            onProductClick={onProductClick}
                        />
                    ))}
            </div>
        </div>
    );
};

export default Slabs;
